// This file is adapted for Weekly view.

import { cn } from '@/lib/utils'
import { formatNumber, formatPercent, formatUSD } from '@/lib/format-utils'
import { useShowDataStore } from '.'
import VisibleDateTrackerCell from '@/components/roi/VisibleDateTrackerCell'
import { getCostBaseline, getProfitBaseline } from '@/lib/dr-utils'
import {
  collectBaselineChangesForDate,
} from '@/components/roi/day-roas-utils'
import { renderBaselineChangeTooltipLines } from '@/components/roi/day-roas-columns'
import {
  createWeeklyDayRoasColumn,
  createAllWeeklyDayRoasColumns,
} from '@/components/roi/day-roas-columns'
import { WEEKLY_ROAS_DAYS } from './constants'
import useRoiTableStore from '@/store/roi-table'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import dayjs from 'dayjs'

export const Td3 = ({ children, avg, growth, absoluteDiff, formatType = 'number' }) => {
  const formattedGrowth = formatPercent(growth)
  const { line1, line2, line3, line4 } = useShowDataStore()
  
  // 格式化绝对值差异
  let formattedAbsoluteDiff = null
  if (absoluteDiff !== undefined && absoluteDiff !== null && !isNaN(absoluteDiff)) {
    if (formatType === 'usd') {
      formattedAbsoluteDiff = formatUSD(Math.abs(absoluteDiff), 0, 0)
    } else if (formatType === 'percent') {
      formattedAbsoluteDiff = formatPercent(Math.abs(absoluteDiff))
    } else {
      formattedAbsoluteDiff = formatNumber(Math.abs(absoluteDiff), 0, 0)
    }
  }
  
  return (
    <div className="flex flex-col text-right">
      {line1 && <div>{children}</div>}
      {line2 && (
        <div className={cn(line1 ? 'text-primary text-xs' : 'text-base')}>
          {avg}
        </div>
      )}
      {line3 && Number.isFinite(growth) && (
        <div
          className={cn(
            'text-xs',
            growth > 0 ? 'text-primary' : 'text-destructive',
          )}
        >
          {growth > 0 ? <>&uarr;</> : <>&darr;</>}
          {formattedGrowth}
        </div>
      )}
      {line4 && formattedAbsoluteDiff !== null && (
        <div
          className={cn(
            'text-xs',
            absoluteDiff > 0 ? 'text-primary' : absoluteDiff < 0 ? 'text-destructive' : '',
          )}
        >
          {absoluteDiff > 0 ? '+' : absoluteDiff < 0 ? '-' : ''}
          {formattedAbsoluteDiff}
        </div>
      )}
    </div>
  )
}

export const columnDate = (country, drBaselines, pidForBaseline, costBaselines, profitBaselines, packageName = null) => ({
  id: 'week',
  accessorFn: (row) => row.week,
  header: 'Week',
  cell: (info) => {
    const week = info.getValue()
    if (!week || !week.days || week.days.length === 0) {
      return null
    }
    const representativeDate = week.days[week.days.length - 1]

    const changesInWeek = []
    week.days.forEach((day) => {
      const dateStr = String(day)
      const dateFormatted = `${dateStr.substring(0, 4)}-${dateStr.substring(
        4,
        6,
      )}-${dateStr.substring(6, 8)}`
      const prevDateStr = String(day - 1)
      const prevDateFormatted = `${prevDateStr.substring(
        0,
        4,
      )}-${prevDateStr.substring(4, 6)}-${prevDateStr.substring(6, 8)}`

      const changeInfo = collectBaselineChangesForDate({
        dateFormatted,
        dayValue: day,
        prevDateValue: prevDateFormatted,
        drBaselines,
        country,
        pidForBaseline,
        packageName,
        costBaselines,
        profitBaselines,
      })

      if (changeInfo) {
        changesInWeek.push(changeInfo)
      }
    })
    const hasChange = changesInWeek.length > 0

    return (
      <VisibleDateTrackerCell date={representativeDate}>
        <div className="relative pl-5">
          {hasChange && (
            <TooltipProvider delayDuration={0}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="absolute bottom-1 left-1 w-3 h-3 bg-green-500 rounded-full" />
                </TooltipTrigger>
                <TooltipContent>
                  <p>Baseline Changed:</p>
                  {changesInWeek.map((change) => (
                    <div key={change.date}>
                      <strong>On {change.date}:</strong>
                      {renderBaselineChangeTooltipLines(change)}
                    </div>
                  ))}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
          <div className="flex flex-col text-left">
            <div>
              {week.days[0]}~{week.days[week.days.length - 1]}
            </div>
            <div className="text-xs text-foreground/80">
              {week.id} ({week.days.length}days)
            </div>
          </div>
        </div>
      </VisibleDateTrackerCell>
    )
  },
})

export const columnDnu = {
  id: 'dnu',
  accessorFn: (row) => {
    const weeklyDnu = row.dnu
    const preWeeklyDnu = row.preDnu
    const dnu = weeklyDnu?.dnu
    const preDnu = preWeeklyDnu?.dnu

    const avg = dnu / row.week?.days.length
    const preAvg = preDnu / row.preWeek?.days.length

    const growth = (dnu - preDnu) / preDnu
    const absoluteDiff = avg - preAvg

    return { dnu, growth, avg, absoluteDiff }
  },
  header: 'DNU',
  cell: (row) => {
    const { dnu, growth, avg, absoluteDiff } = row.getValue()
    const formatted = formatNumber(dnu, 0, 0)
    const formattedAvg = formatNumber(avg, 0, 0)
    return (
      <Td3
        avg={formattedAvg}
        growth={growth}
        absoluteDiff={absoluteDiff}
        formatType="number"
      >
        {formatted}
      </Td3>
    )
  },
}

export const columnInstalls = {
  id: 'installs',
  accessorFn: (row) => {
    const costs = row.costs
    const preCosts = row.preCosts
    const installs = costs?.installs
    const preInstalls = preCosts?.installs

    const avg = installs / costs?.days.length
    const preAvg = preInstalls / preCosts?.days.length

    const growth = (installs - preInstalls) / preInstalls
    const absoluteDiff = avg - preAvg

    return { installs, growth, avg, absoluteDiff }
  },
  header: 'Installs',
  cell: (row) => {
    const { installs, growth, avg, absoluteDiff } = row.getValue()
    const formatted = formatNumber(installs, 0, 0)
    const formattedAvg = formatNumber(avg, 0, 0)
    return (
      <Td3
        avg={formattedAvg}
        growth={growth}
        absoluteDiff={absoluteDiff}
        formatType="number"
      >
        {formatted}
      </Td3>
    )
  },
}

export const columnRevenue = {
  id: 'revenues',
  accessorFn: (row) => {
    const r = row.revenues
    const pre = row.preRevenues

    const avg = r?.total / r?.days.length
    const preAvg = pre?.total / pre?.days.length

    const growth = (avg - preAvg) / preAvg
    const absoluteDiff = avg - preAvg

    return { revenue: r?.total, growth, avg, absoluteDiff }
  },
  header: 'Revenues',
  cell: (row) => {
    const { revenue, growth, avg, absoluteDiff } = row.getValue()
    const formatted = formatUSD(revenue, 0, 0)
    const formattedAvg = formatUSD(avg, 0, 0)
    return (
      <Td3
        avg={formattedAvg}
        growth={growth}
        absoluteDiff={absoluteDiff}
        formatType="usd"
      >
        {formatted}
      </Td3>
    )
  },
}

const CostsHeader = ({ table, costBaselines, country, pid }) => {
  const visibleDates = useRoiTableStore((state) => state.visibleDates)

  let latestDate
  if (visibleDates.size > 0) {
    latestDate = [...visibleDates].reduce((max, current) =>
      current > max ? current : max,
    )
  } else {
    const rows = table.getFilteredRowModel().rows
    latestDate = rows.reduce((maxDate, row) => {
      const week = row.original.week
      const currentDate = week.days[week.days.length - 1]
      return currentDate > maxDate ? currentDate : maxDate
    }, '')
  }

  const headerBaseline = getCostBaseline(
    costBaselines,
    country,
    pid,
    latestDate,
  )
  const formatted = formatUSD(headerBaseline, 0, 0)

  return (
    <div className="text-right">
      Costs
      {headerBaseline > 0 && (
        <span className="text-xs ml-1">({formatted})</span>
      )}
    </div>
  )
}

export const columnCosts = (costBaselines, country, pid) => ({
  id: 'costs',
  accessorFn: (row) => {
    const c = row.costs
    const preC = row.preCosts
    const costs = c?.costs
    const preCosts = preC?.costs

    const avg = costs / c?.days.length
    const preAvg = preCosts / preC?.days.length

    const growth = (costs - preCosts) / preCosts
    const absoluteDiff = avg - preAvg

    return { costs, growth, avg, absoluteDiff, week: row.week }
  },
  header: ({ table }) => (
    <CostsHeader
      table={table}
      costBaselines={costBaselines}
      country={country}
      pid={pid}
    />
  ),
  cell: (row) => {
    const { costs, growth, avg, absoluteDiff, week } = row.getValue()
    const representativeDate = week.days[week.days.length - 1]
    const cellBaseline = getCostBaseline(
      costBaselines,
      country,
      pid,
      representativeDate,
    )
    const formatted = formatUSD(costs, 0, 0)
    const formattedAvg = formatUSD(avg, 0, 0)
    const colorClass =
      cellBaseline > 0
        ? costs < cellBaseline
          ? 'text-destructive'
          : 'text-primary'
        : ''
    return (
      <div className={cn('font-bold text-right', colorClass)}>
        <Td3
          avg={formattedAvg}
          growth={growth}
          absoluteDiff={absoluteDiff}
          formatType="usd"
        >
          {formatted}
        </Td3>
      </div>
    )
  },
})

const ProfitHeader = ({ table, profitBaselines, country, pid }) => {
  const visibleDates = useRoiTableStore((state) => state.visibleDates)

  let latestDate
  if (visibleDates.size > 0) {
    latestDate = [...visibleDates].reduce((max, current) =>
      current > max ? current : max,
    )
  } else {
    const rows = table.getFilteredRowModel().rows
    latestDate = rows.reduce((maxDate, row) => {
      const week = row.original.week
      const currentDate = week.days[week.days.length - 1]
      return currentDate > maxDate ? currentDate : maxDate
    }, '')
  }

  const headerBaseline = getProfitBaseline(
    profitBaselines,
    country,
    pid,
    latestDate,
  )
  const formatted = formatUSD(headerBaseline, 0, 0)

  return (
    <div className="text-right">
      Profit
      {headerBaseline !== 0 && (
        <span className="text-xs ml-1">({formatted})</span>
      )}
    </div>
  )
}

export const columnProfit = (profitBaselines, country, pid) => ({
  id: 'profit',
  accessorFn: (row) => {
    const c = row.costs
    const r = row.revenues
    const profit = r?.total - c?.costs

    const preC = row.preCosts
    const preR = row.preRevenues
    const preProfit = preR?.total - preC?.costs

    const avg = profit / c?.days.length
    const preAvg = preProfit / preC?.days.length
    const growth = (profit - preProfit) / preProfit
    const absoluteDiff = avg - preAvg

    return { profit, growth, avg, absoluteDiff, week: row.week }
  },
  header: ({ table }) => (
    <ProfitHeader
      table={table}
      profitBaselines={profitBaselines}
      country={country}
      pid={pid}
    />
  ),
  cell: (row) => {
    const { profit, growth, avg, absoluteDiff, week } = row.getValue()
    const representativeDate = week.days[week.days.length - 1]
    const cellBaseline = getProfitBaseline(
      profitBaselines,
      country,
      pid,
      representativeDate,
    )
    const formatted = formatUSD(profit, 0, 0)
    const formattedAvg = formatUSD(avg, 0, 0)
    const colorClass =
      cellBaseline !== 0
        ? profit < cellBaseline
          ? 'text-destructive'
          : 'text-primary'
        : ''
    return (
      <div className={cn('font-bold text-right', colorClass)}>
        <Td3
          avg={formattedAvg}
          growth={growth}
          absoluteDiff={absoluteDiff}
          formatType="usd"
        >
          {formatted}
        </Td3>
      </div>
    )
  },
})

export const columnEr = (erBaseLine) => ({
  id: 'er',
  accessorFn: (row) => {
    const er = row.er / 100
    const preEr = row.preEr / 100
    const growth = (er - preEr) / preEr
    const absoluteDiff = er - preEr
    return { er, growth, absoluteDiff }
  },
  header: () => (
    <div>
      ER<span className="text-xs">({formatPercent(erBaseLine)})</span>
    </div>
  ),
  cell: (row) => {
    const { er, growth, absoluteDiff } = row.getValue()
    const formatted = formatPercent(er)
    return (
      <Td3 avg="-" growth={growth} absoluteDiff={absoluteDiff} formatType="percent">
        {formatted}
      </Td3>
    )
  },
})

const bindTd3 = (day) => (drBaselines, country, pid, packageName = null) =>
  createWeeklyDayRoasColumn(day, drBaselines, country, pid, packageName, Td3)

export const columnDay0Roas = bindTd3(0)
export const columnDay3Roas = bindTd3(3)
export const columnDay7Roas = bindTd3(7)
export const columnDay14Roas = bindTd3(14)
export const columnDay30Roas = bindTd3(30)
export const columnDay60Roas = bindTd3(60)
export const columnDay90Roas = bindTd3(90)
export const columnDay120Roas = bindTd3(120)
export const columnDay150Roas = bindTd3(150)

export const createAllWeeklyDayRoasColumnsWithTd3 = (
  drBaselines,
  country,
  pid,
  packageName,
  onDayClick = null,
) =>
  createAllWeeklyDayRoasColumns(
    drBaselines,
    country,
    pid,
    packageName,
    Td3,
    onDayClick,
    WEEKLY_ROAS_DAYS,
  )

export const columnCPI = {
  id: 'cpi',
  accessorFn: (row) => {
    const c = row.costs
    const cpi = c?.costs / row.dnu?.dnu

    const preC = row.preCosts
    const preCpi = preC?.costs / row.preDnu?.dnu

    const growth = (cpi - preCpi) / preCpi
    const absoluteDiff = cpi - preCpi

    return { cpi, growth, absoluteDiff }
  },
  header: 'CPI(DNU)',
  cell: (row) => {
    const { cpi, growth, absoluteDiff } = row.getValue()
    const formatted = formatUSD(cpi, 4, 4)
    return (
      <Td3 avg="-" growth={growth} absoluteDiff={absoluteDiff} formatType="usd">
        {formatted}
      </Td3>
    )
  },
}

export const columnCPIInstalls = {
  id: 'cpiInstalls',
  accessorFn: (row) => {
    const c = row.costs
    const cpi = c?.costs / c?.installs

    const preC = row.preCosts
    const preCpi = preC?.costs / preC?.installs

    const growth = (cpi - preCpi) / preCpi
    const absoluteDiff = cpi - preCpi

    return { cpi, growth, absoluteDiff }
  },
  header: 'CPI(Installs)',
  cell: (row) => {
    const { cpi, growth, absoluteDiff } = row.getValue()
    const formatted = formatUSD(cpi, 4, 4)
    return (
      <Td3 avg="-" growth={growth} absoluteDiff={absoluteDiff} formatType="usd">
        {formatted}
      </Td3>
    )
  },
}
